import { useState } from 'react';
import { SEO } from '../components/SEO';
import { FiChevronDown } from 'react-icons/fi';

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: 'How quickly can I withdraw my winnings?',
    answer: 'Withdrawal times vary significantly by payment method. E-wallets like PayPal, Skrill, and Neteller typically process within 24 hours, sometimes instantly. Bank transfers usually take 2-5 business days, while credit/debit card withdrawals can take 1-3 days. Always check the casino\'s specific payout policy before depositing.'
  },
  {
    question: 'What are wagering requirements?',
    answer: 'Wagering requirements determine how many times you must bet your bonus amount before you can withdraw. For example, if you receive a £100 bonus with 35x wagering, you need to wager £3,500 total before cashing out. Lower wagering requirements (under 40x) are generally better. Some casinos offer zero-wagering bonuses, which are the most player-friendly.'
  },
  {
    question: 'Are UK casino bonuses worth it?',
    answer: 'Yes, if you understand the terms. Look for bonuses with lower wagering requirements (under 40x), reasonable time limits (at least 30 days), and games that contribute fully to wagering. Slots usually contribute 100%, while table games often contribute less. Always read the full terms and conditions before claiming any bonus.'
  },
  {
    question: 'Do I need to verify my account?',
    answer: 'Yes, all UK-licensed casinos require KYC (Know Your Customer) verification. You\'ll typically need to provide a government-issued ID (passport or driving license), proof of address (utility bill or bank statement from the last 3 months), and sometimes payment method verification. This is a legal requirement and helps prevent fraud and money laundering.'
  },
  {
    question: 'What is the minimum deposit?',
    answer: 'Minimum deposits vary by casino, typically ranging from £10 to £20. Some casinos offer lower minimums to attract new players. Keep in mind that lower minimum deposits might come with smaller bonus amounts or different terms. Always check the specific offer details.'
  },
  {
    question: 'Can I play on mobile?',
    answer: 'Yes, all modern UK casinos offer mobile-optimized websites, and many have dedicated apps for iOS and Android. Mobile casinos should offer the same games, bonuses, and features as desktop versions. Look for casinos with high mobile experience scores in our reviews.'
  },
  {
    question: 'Are online casinos safe?',
    answer: 'UK-licensed casinos are heavily regulated by the UK Gambling Commission, which ensures fair play, secure payments, and responsible gambling practices. Look for the UKGC license number on the casino\'s website. Additionally, reputable casinos use SSL encryption to protect your personal and financial data.'
  },
  {
    question: 'What happens if I have a problem?',
    answer: 'First, contact the casino\'s customer support team directly. Most issues can be resolved quickly. If you\'re not satisfied, you can escalate to the casino\'s complaints department. As a last resort, you can contact the UK Gambling Commission or use an Alternative Dispute Resolution (ADR) service like IBAS (Independent Betting Adjudication Service).'
  },
  {
    question: 'Do casinos charge withdrawal fees?',
    answer: 'Most UK-licensed casinos don\'t charge fees for standard withdrawal methods like bank transfers or e-wallets. However, some payment providers may charge their own fees. Always check the casino\'s terms for specific fee information. Premium withdrawal methods or expedited processing may incur charges.'
  },
  {
    question: 'Can I claim multiple bonuses?',
    answer: 'This depends on the casino\'s terms. Welcome bonuses are typically one-time offers for new players. However, many casinos offer ongoing promotions, reload bonuses, and loyalty rewards for existing players. Always check the terms to see if you can claim multiple bonuses and whether they can be combined.'
  }
];

export const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  };

  return (
    <>
      <SEO
        title="Frequently Asked Questions | UK Casino FAQ | Your Casinos"
        description="Answers to common questions about UK casino bonuses, wagering requirements, withdrawals, verification, and more."
        canonical="/faq"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-black py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-gray-400 mb-12">
            Everything you need to know about UK casino offers, bonuses, and withdrawals.
          </p>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="glass-card overflow-hidden">
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-black/30 transition-colors"
                >
                  <span className="font-medium text-white pr-4 text-lg">{item.question}</span>
                  <FiChevronDown
                    className={`flex-shrink-0 text-primary-green transition-transform ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                    size={24}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-4 text-gray-300 leading-relaxed">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

